/** Unified pricing page route (see `app/packs.tsx`). */
export const ACCOUNT_ADMIN_PACKS_PATH = '/packs' as const

/** Enterprise sales contact page (see `app/enterprise.tsx`). */
export { ENTERPRISE_CONTACT_PATH } from '../../packs/constants/enterpriseContact.constants'

/** Public org admin / create-join org surface (Expo route `app/account-admin.tsx`). */
export const ACCOUNT_ADMIN_PUBLIC_ROUTE = '/account-admin' as const

/**
 * When `false`, Branding / Members / Settings tabs are unlocked for all users
 * regardless of planTier. Flip to `true` to re-enable billing-based gating.
 */
export const BILLING_GATE_ENABLED = false

/** Keep aligned with server `B2B_LOGO_MAX_COUNT` (r2Upload.constants.js). */
const B2B_ORG_LOGO_MAX = 1

/** Keep aligned with server `UPLOAD_MAX_BYTES` (r2Upload.constants.js). */
export const B2B_ORG_LOGO_MAX_FILE_BYTES = 2 * 1024 * 1024

/** Primary nav destinations for organization admin (drawer + content). */
export const ACCOUNT_ADMIN_NAV = {
  START_HERE: 'start_here',
  BRANDING: 'branding',
  MEMBERS: 'members',
  SETTINGS: 'settings',
  /** Web only: plan picker & upgrades (hidden on native drawer). */
  PLAN_BILLING: 'plan_billing',
  /** Web only: subscription, portal, and invoice profile (hidden on native drawer). */
  INVOICE_DETAILS: 'invoice_details',
} as const

export type AccountAdminNavId = (typeof ACCOUNT_ADMIN_NAV)[keyof typeof ACCOUNT_ADMIN_NAV]

/** Drawer item order — Plan is second so admins complete it before the other tabs. */
export const ACCOUNT_ADMIN_NAV_ITEMS: AccountAdminNavId[] = [
  ACCOUNT_ADMIN_NAV.START_HERE,
  ACCOUNT_ADMIN_NAV.PLAN_BILLING,
  ACCOUNT_ADMIN_NAV.INVOICE_DETAILS,
  ACCOUNT_ADMIN_NAV.BRANDING,
  ACCOUNT_ADMIN_NAV.MEMBERS,
  ACCOUNT_ADMIN_NAV.SETTINGS,
]

/** Drawer / deep links: billing tabs are web-only (hidden on native). */
export const ACCOUNT_ADMIN_WEB_ONLY_NAV_IDS: ReadonlySet<AccountAdminNavId> = new Set([
  ACCOUNT_ADMIN_NAV.PLAN_BILLING,
  ACCOUNT_ADMIN_NAV.INVOICE_DETAILS,
])

/** Stops for the branding color picker hue strip (expo-linear-gradient) */
export const BRAND_COLOR_HUE_GRADIENT_STOPS = [
  '#FF0000',
  '#FFFF00',
  '#00FF00',
  '#00FFFF',
  '#0000FF',
  '#FF00FF',
  '#FF0000',
] as const

/** Preset swatches for primary/secondary brand colors (hex) */
export const BRAND_COLOR_SWATCHES = [
  '#5B21B6',
  '#1D4ED8',
  '#047857',
  '#B45309',
  '#B91C1C',
  '#0F766E',
  '#7C3AED',
  '#DB2777',
  '#1E293B',
  '#CA8A04',
  '#2563EB',
  '#16A34A',
] as const

export const ACCOUNT_ADMIN_COPY = {
  /** Drawer parent row when `siteMenuLinks` has no organizations entry. */
  ORG_DRAWER_PARENT_FALLBACK: 'Organizations',
  FRIENDS_ENTRY_LABEL: 'Manage organization',
  FRIENDS_ENTRY_HINT: 'Branding & members',
  START_HERE: 'Start here',
  START_HERE_INTRO:
    'Use the organization navigation to move between Start here, Branding, Members, and Settings. Then:',
  OPEN_MENU: 'Open menu',
  BRANDING: 'Branding',
  MEMBERS: 'Members',
  SETTINGS: 'Settings',
  PLAN_BILLING: 'Plan',
  PLAN_BILLING_INTRO:
    'Upgrade your organization on the web, manage payment methods, and view invoices in the Stripe customer portal.',
  PLAN_BILLING_CURRENT: 'Current plan',
  PLAN_BILLING_RENEWS: 'Renews or period ends',
  PLAN_BILLING_STATUS: 'Subscription status',
  PLAN_BILLING_UPGRADE_BUSINESS: 'Upgrade to Business',
  PLAN_BILLING_UPGRADE_ENTERPRISE: 'Upgrade to Enterprise',
  PLAN_BILLING_PORTAL: 'Manage billing & invoices',
  PLAN_BILLING_WEB_ONLY: 'Plans are available on the web.',
  INVOICE_DETAILS: 'Billing & invoice',
  INVOICE_DETAILS_WEB_ONLY: 'Billing & invoice are available on the web.',
  MANAGE_PLAN_BILLING: 'Manage plan',
  COMPARE_PLANS: 'Compare plans',
  NO_ORG_TITLE: 'No organization yet',
  NO_ORG_SUBTITLE: 'Create one to manage branding and members, or join with a code.',
  CREATE_PLACEHOLDER: 'Organization name',
  CREATE_CTA: 'Create organization',
  JOIN_PLACEHOLDER: 'Join code',
  JOIN_CTA: 'Join',
  MEMBER_ONLY_TITLE: 'Member access',
  MEMBER_ONLY_MESSAGE:
    'Only organization admins can change branding and settings. Contact your admin if you need updates.',
  /** Member opened this org's invite link: already joined — not an "accept invite" step. */
  MEMBER_INVITE_RETURN_TITLE: 'You are already in this organization',
  MEMBER_INVITE_RETURN_MESSAGE:
    'This link is for teammates who still need to join. You are signed in as a member, so there is nothing to accept. Only admins can change branding and settings.',
  B2B_LOGO_MAX_COUNT: B2B_ORG_LOGO_MAX,
  BRAND_IMAGERY_SECTION_TITLE: 'Brand imagery',
  UPLOAD_LOGOS: 'Upload logo',
  LOGO_TILE_LABEL: 'Logo',
  LOGO_TILE_EMPTY: 'Add a logo',
  BANNER_TILE_LABEL: 'Squad banner',
  BANNER_TILE_EMPTY: 'Add a banner',
  UPLOAD_BANNER: 'Upload banner',
  REPLACE_BANNER: 'Replace banner',
  REMOVE_BANNER: 'Remove banner',
  REMOVE_BANNER_CONFIRM_TITLE: 'Remove this banner?',
  REMOVE_BANNER_CONFIRM_MESSAGE: 'The squad will fall back to the brand color gradient.',
  /** Reserved for future fine-print; empty hides the line. */
  LOGOS_UPLOAD_LIMITS_PLACEHOLDER: '',
  LOGOS_UPLOAD_HINT:
    'PNG, JPG, WebP, or SVG. Max 2MB. Square (1:1), 512–1024px on each side. Transparent PNG preferred.',
  BANNER_UPLOAD_HINT:
    'PNG, JPG, or WebP. Max 2MB. Wide 4:1 ratio — 1600×400px recommended, min 1200×300px.',
  USE_BANNER_BACKGROUND_LABEL: 'Use banner image as squad background',
  USE_BANNER_BACKGROUND_HINT:
    'When on, the squad hero shows your banner image instead of the primary/secondary color gradient.',
  USE_BANNER_BACKGROUND_DISABLED_HINT: 'Upload a banner image first to enable this toggle.',
  USE_BANNER_BACKGROUND_SAVED: 'Saved',
  REMOVE_LOGO: 'Remove',
  /** Triggers authenticated API download (no public CDN URL in the address bar on web). */
  DOWNLOAD_LOGO: 'Download',
  DOWNLOAD_LOGO_ERROR: 'Could not download the image',
  REMOVE_LOGO_CONFIRM_TITLE: 'Remove this logo?',
  REMOVE_LOGO_CONFIRM_MESSAGE: 'It will no longer appear for your organization.',
  LOGO_FILE_TOO_LARGE: 'That image is over the 2MB upload limit. Try a smaller file.',
  SAVE_COLORS: 'Save colors',
  COLORS_SAVED: 'Colors saved',
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
  COLOR_PRESETS: 'Presets',
  COLOR_PALETTE_LABEL: 'Palette',
  COLOR_PALETTE_SATURATION_A11Y: 'Saturation and brightness. Drag to choose color.',
  COLOR_PALETTE_HUE_A11Y: 'Hue. Drag to shift color.',
  CUSTOM_HEX_LABEL: 'Custom (hex)',
  HEX_PLACEHOLDER: '#RRGGBB',
  JOIN_CODE: 'Join code',
  /** Members tab when a Football 2026 squad is linked: primary row is org join (same code body as squad when synced). */
  SQUAD_JOIN_CODE: 'Squad join code',
  /** Members tab: how teammates join (before join code / link blocks). */
  ADD_MEMBERS_TITLE: 'Add teammates',
  ADD_MEMBERS_BODY:
    'Share the invitation link below. Teammates sign in to Zporti and open the link to join this organization.',
  /** Members tab when a linked Football 2026 squad exists: primary path is org membership (includes squad). */
  ADD_MEMBERS_BODY_WC_SINGLE_PRIMARY:
    'Share the invitation link below so teammates sign in and join this organization — they are added to your linked Football 2026 squad automatically. For friends outside the organization, share a squad invite from Football 2026 → Squads (not from this page).',
  MEMBERS_DIRECTORY: 'Member directory',
  MEMBERS_ADMIN_HINT:
    'Admins can manage branding, members, and settings. Use Make admin / Demote / Remove in the Actions column.',
  MEMBERS_TABLE_INDEX: '#',
  MEMBERS_TABLE_NAME: 'Name',
  MEMBERS_TABLE_EMAIL: 'Email',
  MEMBERS_TABLE_ROLE: 'Role',
  MEMBERS_TABLE_JOINED: 'Joined',
  MEMBERS_TABLE_ACTIONS: 'Actions',
  /** Shown in the Actions row when there are no buttons (e.g. your own account). */
  MEMBERS_TABLE_NO_ACTIONS_VISIBLE: 'No actions',
  /** Screen reader when a row has no role/remove buttons (e.g. your own row). */
  MEMBER_ACTION_LOADING_A11Y: 'Updating member',
  MEMBERS_BULK_REMOVE_LOADING_A11Y: 'Removing selected members',
  MEMBERS_TABLE_NO_ACTIONS_A11Y:
    'No actions. You cannot edit your own row, or there is nothing to change for this member.',
  ROLE_LABEL_ADMIN: 'Admin',
  ROLE_LABEL_MEMBER: 'Member',
  MEMBERS_EMPTY: 'No members yet.',
  MEMBERS_DIRECTORY_SEARCH_PLACEHOLDER: 'Search by name or email',
  MEMBERS_DIRECTORY_SEARCH_A11Y: 'Search members by name or email',
  MEMBERS_DIRECTORY_FILTER_ALL: 'All',
  MEMBERS_DIRECTORY_FILTER_ADMIN: 'Admins',
  MEMBERS_DIRECTORY_FILTER_MEMBER: 'Members',
  MEMBERS_DIRECTORY_SORT_LABEL: 'Sort',
  MEMBERS_DIRECTORY_SORT_DEFAULT: 'Default',
  MEMBERS_DIRECTORY_SORT_NAME_ASC: 'Name A–Z',
  MEMBERS_DIRECTORY_SORT_NAME_DESC: 'Name Z–A',
  MEMBERS_DIRECTORY_SORT_JOINED_NEWEST: 'Newest',
  MEMBERS_DIRECTORY_SORT_JOINED_OLDEST: 'Oldest',
  MEMBERS_DIRECTORY_NO_RESULTS: 'No members match your search or filters.',
  membersDirectoryShowingCount: (shown: number, total: number) =>
    `Showing ${shown} of ${total}`,
  /** Appended to display name in member table for the signed-in user (single line). */
  MEMBER_ROW_YOU_INLINE: ' (You)',
  INVITE_LINK: 'Invitation link',
  INVITE_LINK_HINT: 'Share this link so teammates can sign in and join your organization.',
  INVITE_LINK_HINT_WC:
    'This link opens the organization join flow. After they sign in, they join the org and your linked Football 2026 squad together.',
  INVITE_LINK_QR_HINT: 'Scan with a phone camera to open this link.',
  INVITE_LINK_QR_A11Y:
    'QR code for the invitation link. Scan with a phone camera to open the join page.',
  COPY_INVITE_LINK: 'Copy link',
  /** Admin button on Members tab: opens the org's linked Football 2026 squad page (read-only preview). */
  VIEW_LINKED_SQUAD: 'View squad page',
  VIEW_LINKED_SQUAD_A11Y: 'Open the linked Football 2026 squad to preview results',
  SEATS: 'Seats',
  SEATS_UNLIMITED: 'Unlimited',
  MAKE_ADMIN: 'Make admin',
  MAKE_ADMIN_TITLE: 'Make this member an admin?',
  MAKE_ADMIN_MESSAGE:
    'They will be able to manage branding, members, and organization settings.',
  DEMOTE_ADMIN: 'Demote',
  DEMOTE_ADMIN_TITLE: 'Demote this admin?',
  DEMOTE_ADMIN_MESSAGE: 'They will keep access as a member but lose admin controls.',
  REMOVE_MEMBER: 'Remove',
  MEMBERS_BULK_SELECT: 'Select multiple',
  MEMBERS_BULK_CANCEL_SELECT: 'Cancel selection',
  MEMBERS_BULK_REMOVE: 'Remove selected',
  MEMBERS_BULK_REMOVE_TITLE: 'Remove selected members?',
  MEMBERS_BULK_REMOVE_MESSAGE:
    'They will lose access to this organization (and the linked Football 2026 squad, if any). This cannot be undone.',
  MEMBERS_BULK_REMOVE_CONFIRM: 'Remove',
  MEMBERS_BULK_ROW_CHECKBOX_A11Y: 'Select member for bulk remove',
  MEMBERS_BULK_CANCEL_DIALOG: 'Cancel',
  MEMBERS_BULK_REMOVE_FAILED: 'Bulk remove failed',
  MEMBERS_INVITE_LINK_COPIED: 'Link copied',
  /** Snackbar after bulk remove with skips. Args: removed count, skipped count. */
  membersBulkRemoveSummary: (removed: number, skipped: number) =>
    `Removed ${removed}; skipped ${skipped}`,
  /** Snackbar after a clean bulk remove. */
  membersBulkRemoveDone: (removed: number) => `Removed ${removed}`,
  /** Header checkbox in bulk mode: selects every row except the signed-in user. */
  MEMBERS_BULK_SELECT_ALL_A11Y: 'Select or clear all members except yourself',
  ACCOUNT_NAME: 'Organization name',
  SAVE_NAME: 'Save name',
  /** Settings (and copy elsewhere): canonical name for the org ↔ Football 2026 squad link. */
  WC_LINKED_SQUAD_SECTION_TITLE: 'Linked Football 2026 squad',
  /** Settings: squad is linked — members use org invite only. */
  WC_LINKED_SQUAD_SECTION_BODY_LINKED:
    'This is the same squad you see under Football 2026 → Squads. Change the squad name here or on that squad’s screen. Share the organization invite link from the Members tab: new members join the org first, are added to this squad automatically, then open the squad.',
  /** Settings: no squad linked yet — paste a squad code to link. */
  WC_LINKED_SQUAD_SECTION_BODY_NOT_LINKED:
    'Paste a Football 2026 squad invite code to link that squad to this organization. After linking, anyone who joins using the organization invite link on the Members tab is added to the squad automatically.',
  WC_LINKED_SQUAD_NAME_LABEL: 'Squad name',
  WC_LINKED_SQUAD_INVITE_CODE_LABEL: 'Squad invite code',
  WC_LINKED_SQUAD_CODE_PLACEHOLDER: 'Paste squad invite code',
  WC_LINKED_SQUAD_SAVE_NAME: 'Save squad name',
  /** First-time link: saves pasted squad code as the linked default squad. */
  WC_LINKED_SQUAD_LINK_SQUAD: 'Link squad',
  /** Settings: invite block when a WC squad is linked (org join via link; squad auto-added). */
  ORG_ONLY_JOIN_CODE_TITLE: 'Organization invite',
  ORG_ONLY_JOIN_CODE_BODY:
    'Share the invite link so people join the organization first; they are added to the squad automatically and can open it from Football 2026 → Squads.',
  COPY_ORG_INVITE_LINK: 'Copy invite link',
  PLAN_TIER: 'Plan',
  MEMBERSHIP_POLICY_TITLE: 'Member limit',
  MEMBERSHIP_POLICY_UNLIMITED_LABEL: 'Unlimited members',
  MEMBERSHIP_POLICY_UNLIMITED_APPROVED_HELP:
    'Unlimited invite-link joins are enabled for this organization after Zporti approval.',
  MEMBERSHIP_POLICY_FROM_PLAN_HELP:
    'Your plan includes unlimited invite-link members. No action needed.',
  MEMBERSHIP_POLICY_PENDING_HELP:
    'Your request is pending. Zporti will enable unlimited members after review.',
  MEMBERSHIP_POLICY_LIMITED_HELP:
    'When unchecked, invite-link joins are limited by your seat limit (default 100 on Starter).',
  MEMBERSHIP_POLICY_ENABLE_DIALOG_TITLE: 'Request unlimited members?',
  MEMBERSHIP_POLICY_ENABLE_DIALOG_MESSAGE:
    'This sends a request to Zporti. We will review and enable unlimited invite-link members for your organization. You can continue using your current seat limit until then. Send the request now?',
  MEMBERSHIP_POLICY_ENABLE_DIALOG_CONFIRM: 'Send request',
  MEMBERSHIP_POLICY_REQUEST_SENT: 'Request sent',
  MEMBERSHIP_POLICY_CANCEL_REQUEST_LINK: 'Cancel request',
  MEMBERSHIP_POLICY_CANCEL_DIALOG_TITLE: 'Cancel unlimited-members request?',
  MEMBERSHIP_POLICY_CANCEL_DIALOG_MESSAGE:
    'This withdraws your pending request. Zporti will be notified that you cancelled.',
  MEMBERSHIP_POLICY_CANCEL_CONFIRM: 'Cancel request',
  MEMBERSHIP_POLICY_CANCEL_DONE: 'Request cancelled',
  BILLING_GATE_LOCKED_TITLE: 'Complete billing to unlock',
  BILLING_GATE_LOCKED_BODY:
    'Set up your organization plan to access branding, members, and settings.',
  BILLING_GATE_GO_TO_BILLING: 'Go to Plan',
  DEACTIVATE: 'Deactivate organization',
  DEACTIVATE_CONFIRM_TITLE: 'Deactivate organization?',
  DEACTIVATE_CONFIRM_MESSAGE:
    'Members will lose organization benefits until the organization is reactivated by support.',
  INACTIVE_TITLE: 'Inactive',
  INACTIVE_BODY:
    'This organization is deactivated. You can reactivate it if this was a mistake.',
  REACTIVATE: 'Reactivate',
  REMOVE_MEMBER_TITLE: 'Remove member?',
  REMOVE_MEMBER_MESSAGE: 'They will lose access to this organization.',
  /** Self-service leave (members always; admins only if another admin exists). */
  LEAVE_ORG_TITLE: 'Leave this organization?',
  LEAVE_ORG_MESSAGE:
    'You will lose access to branding and member tools for this org. You can create or join another organization afterward.',
  LEAVE_ORG_CONFIRM: 'Leave organization',
  LEAVE_ORG_MEMBER_HINT:
    'To stop using this organization’s benefits, you can leave. Your admin can add you again with an invite.',
  LEAVE_ORG_DONE_TITLE: 'You left the organization',
  LEAVE_ORG_DONE_MESSAGE: 'You can create a new organization or join one with a code from this page.',
  /** Settings: sole admin cannot self-leave until another admin exists (matches API). */
  LEAVE_ORG_SOLE_ADMIN_HINT:
    'You are the only admin. Promote another member to admin in Members, then you can leave.',
  /** Settings danger zone: after deactivation, permanent delete UI appears on this tab. */
  DEACTIVATE_PERMANENT_DELETE_FOOTNOTE:
    'After deactivation, a permanent delete section with a confirmation field appears on this tab under Leave organization.',
  /** Shown only when org is inactive — permanent delete with name confirmation. */
  DELETE_ORG_SECTION_TITLE: 'Delete permanently',
  DELETE_ORG_SECTION_BODY:
    'This removes the organization and all memberships from our systems. Type the exact phrase below: the word delete in lowercase, one space, then your organization name exactly as saved.',
  DELETE_ORG_NAME_LABEL: 'Type this phrase exactly',
  DELETE_ORG_BUTTON: 'Delete organization forever',
  DELETE_ORG_DIALOG_TITLE: 'Delete this organization forever?',
  DELETE_ORG_DIALOG_MESSAGE:
    'This cannot be undone. All members will lose access and branding data will be removed.',
} as const

/** Pixel width/height for the invitation-link QR on the Members tab (readable on most phones). */
export const ACCOUNT_ADMIN_INVITE_LINK_QR_SIZE = 168

/** Max height (px) of the members directory scroll body (spec T-29). */
export const B2B_MEMBERS_TABLE_SCROLL_MAX_HEIGHT = 440

/** testIDs for members directory table (Storybook play + Vitest). */
export const ACCOUNT_MEMBERS_TAB_TEST_IDS = {
  TABLE_SCROLL_BODY: 'account-members-table-scroll-body',
  TABLE_HEADER: 'account-members-table-header',
  rowIndex: (displayIndex: number) => `account-members-row-index-${displayIndex}`,
} as const

/** Shown on site-surface org admin to orient new admins (branding → members → settings). */
export const ACCOUNT_ADMIN_SITE_SETUP_GUIDE = {
  TITLE: 'Suggested setup order',
  LINE_BRANDING_BEFORE: 'Set your ',
  LINE_BRANDING_LINK: 'logos and brand colors',
  LINE_BRANDING_AFTER: ' so members recognize your organization.',
  LINE_MEMBERS_BEFORE: 'Invite your team from ',
  LINE_MEMBERS_AFTER: ' using your invitation link or join code.',
  LINE_SETTINGS_BEFORE: 'Review the organization name and plan in ',
  LINE_SETTINGS_AFTER: '.',
} as const
